// KPI detail drawer orchestrator. Mounts SlideOverDrawer using its
// title/subtitle/actions slots so the drawer renders a single <h2> and a
// single close affordance for a11y. Subtitle carries the
// RAG pill + the deferred meta line. The separate
// KpiDetailHeader component has been retired — the content it owned was
// thin enough that inlining it keeps the drawer readable without
// duplicating the drawer's own heading/close affordances.
//
// Fetches this KPI's measurements + reports directly so the drawer stays
// self-contained. Owns the layer-2 KpiLogPeriodEntry modal state —
// opened from the header's "+ Log period" (fresh mode) or a history
// row's amend icon (pre-filled). Layer-2 takes Esc precedence by hiding
// the parent drawer while the modal is open.

import { useMemo, useState, lazy, Suspense, type JSX } from "react";
import Button from "../../../../common/Button";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import SlideOverDrawer from "../../../../common/SlideOverDrawer";
import TrackingStatusBadge from "../../shared/TrackingStatusBadge";
import {
  useCreateTrackingItem,
  useTrackingItemsByParent,
  useUpdateTrackingItem,
} from "../../../../../hooks/useTracking";
import { useUserDisplays } from "../../../../../hooks/useUsers";
import { trackingApi } from "../../../../../services/api";
import type {
  CreateKpiMeasurementInput,
  CreateKpiReportInput,
  Kpi,
  KpiMeasurement,
  KpiReport,
  UserDisplay,
} from "../../../../../types";
import KpiDetailStats from "./KpiDetailStats";
import KpiHistoryTable, {
  type KpiHistoryAmendData,
  type KpiHistoryRange,
} from "./KpiHistoryTable";
import KpiLogPeriodEntry, {
  type KpiLogPeriodInitialData,
} from "./KpiLogPeriodEntry";
import KpiMeasurementCriteria from "./KpiMeasurementCriteria";
import { parseKpiMeasurementValue } from "../parseKpiMeasurementValue";

// Lazy-loaded so recharts ships as an on-demand chunk fetched only when a KPI
// detail drawer is opened, not in the eager bundle.
const KpiMonthlyChart = lazy(() => import("./KpiMonthlyChart"));
import { KPI_BOOLEAN_MET, KPI_BOOLEAN_NOT_MET } from "../../../../../types";
import { dedupeMeasurementsByPeriod } from "../../../../../lib/kpi/summary";

// Ownership segment (Supplier-owned / Customer-owned / Joint) is deferred
// — the field does not yet exist on the Kpi type. Removing the prop
// surface entirely keeps the current API honest; it will be re-added
// alongside the backend + form work.

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// `updatedByName` / `updatedByOrg` no longer read off the report —
// user attribution now resolves via `useUserDisplays(updatedBy)`
// The `ExtendedReport` fake fields were retired when the real
// hook landed.

function formatDate(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${pad2(d.getUTCDate())} ${MONTH_LABELS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function lastUpdatedSegment(
  report: KpiReport | null | undefined,
  resolvedUser: UserDisplay | undefined,
): string | null {
  if (!report) return null;
  const dateLabel = formatDate(report.updatedAt);
  if (!dateLabel) return null;
  // Only append " by {name} ({org})" when the user has been resolved.
  // Loading, disabled, or cross-tenant IDs fall through to the degraded
  // form — never "by ..." or "Unknown user" mid-load.
  const name = resolvedUser?.name?.trim() ?? "";
  const org = resolvedUser?.organisation?.trim() ?? "";
  let who = "";
  if (name && org) who = ` by ${name} (${org})`;
  else if (name) who = ` by ${name}`;
  // "Last updated" → "Last logged": "Updated" reads as a
  // definition edit; "logged" reads as a period figure being
  // entered, which is what actually happens.
  return `Last logged ${dateLabel}${who}`;
}

function ownershipLabel(
  ownership: Kpi["ownership"],
): string | null {
  switch (ownership) {
    case "supplier":
      return "Supplier-owned";
    case "customer":
      return "Customer-owned";
    case "joint":
      return "Joint";
    default:
      return null;
  }
}

function buildMetaSegments(
  kpi: Kpi,
  latestReport: KpiReport | null | undefined,
  resolvedUser: UserDisplay | undefined,
): string[] {
  // Clause reference removed from the drawer meta; it's
  // duplicated in the Measurement criteria card below. The ownership
  // + "Last logged" segments are the drawer's identity line now.
  const segments: string[] = [];
  const ownership = ownershipLabel(kpi.ownership ?? null);
  if (ownership) segments.push(ownership);
  const updatedSeg = lastUpdatedSegment(latestReport, resolvedUser);
  if (updatedSeg) {
    segments.push(updatedSeg);
  } else if (ownership) {
    // Ownership present but nothing logged yet — tell the user.
    segments.push("Not yet logged");
  }
  return segments;
}

export interface KpiDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  kpi: Kpi | null;
  contractId: string;
  onEdit: (kpi: Kpi) => void;
  range?: KpiHistoryRange;
}

function latestByPeriod<T extends { period: string }>(items: T[]): T | undefined {
  if (items.length === 0) return undefined;
  return [...items].sort((a, b) => a.period.localeCompare(b.period)).pop();
}

function parseTargetNumeric(target: string): number | null {
  const m = /(-?\d+(?:\.\d+)?)/u.exec(target);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

export default function KpiDetailDrawer({
  isOpen,
  onClose,
  kpi,
  contractId,
  onEdit,
  range = "12m",
}: KpiDetailDrawerProps): JSX.Element | null {
  const measurementsQuery = useTrackingItemsByParent(
    contractId,
    "kpi-measurement",
    kpi?.id ?? "",
  );
  const reportsQuery = useTrackingItemsByParent(
    contractId,
    "kpi-report",
    kpi?.id ?? "",
  );

  const measurements: KpiMeasurement[] = useMemo(
    () =>
      (measurementsQuery.data?.items ?? []).filter(
        (i): i is KpiMeasurement => i.type === "kpi-measurement",
      ),
    [measurementsQuery.data],
  );
  const reports: KpiReport[] = useMemo(
    () =>
      (reportsQuery.data?.items ?? []).filter(
        (i): i is KpiReport => i.type === "kpi-report",
      ),
    [reportsQuery.data],
  );
  const isTruncated =
    Boolean(measurementsQuery.data?.truncated) ||
    Boolean(reportsQuery.data?.truncated);

  const latestReport = useMemo(() => latestByPeriod(reports), [reports]);

  // Resolve the latest report's `updatedBy` to a display name / org
  // via the shared by-id cache. Hook call must precede any
  // conditional early return (react-hooks/rules-of-hooks).
  const updatedById = latestReport?.updatedBy;
  const userLookup = useUserDisplays(updatedById ? [updatedById] : []);
  const resolvedUser: UserDisplay | undefined = updatedById
    ? userLookup.data.get(updatedById)
    : undefined;

  const isBooleanKpi = (kpi?.valueType ?? "percentage") === "boolean";
  const monthlySeries = useMemo(() => {
    // Dedupe (parentId, period) before plotting — storage can still
    // contain duplicates from legacy writes, and Recharts would render
    // both points on the same x-tick otherwise. Keep the latest-logged
    // row per period; same rule the history table uses.
    const sorted = dedupeMeasurementsByPeriod(measurements).sort((a, b) =>
      a.period.localeCompare(b.period),
    );
    if (isBooleanKpi) {
      // Map met → 100, not_met → 0 so the chart renders on a [0, 100]
      // domain. Unknown values fall through to null; the chart filters
      // them out like any other non-numeric point.
      return sorted.map((m) => {
        const v = m.value?.trim().toLowerCase();
        if (v === KPI_BOOLEAN_MET) return { period: m.period, value: 100 };
        if (v === KPI_BOOLEAN_NOT_MET) return { period: m.period, value: 0 };
        return { period: m.period, value: null };
      });
    }
    return sorted.map((m) => ({
      period: m.period,
      value: parseKpiMeasurementValue(m.value).numeric,
    }));
  }, [measurements, isBooleanKpi]);

  const createMeasurement = useCreateTrackingItem();
  const createReport = useCreateTrackingItem();
  const updateMeasurementMut = useUpdateTrackingItem();
  const updateReportMut = useUpdateTrackingItem();

  const [logPeriodOpen, setLogPeriodOpen] = useState(false);
  const [amendData, setAmendData] = useState<KpiLogPeriodInitialData | null>(
    null,
  );

  if (!isOpen || !kpi) return null;

  const isLoading = measurementsQuery.isPending || reportsQuery.isPending;
  const targetNumeric = parseTargetNumeric(kpi.target);

  // "+ Log period" always opens a fresh-period entry defaulting to the
  // next unlogged period (computed inside the modal). Amend mode is
  // reached exclusively via the pencil icon on history rows — the
  // button is for forward-logging, not retro-editing the current period.
  const handleOpenLogPeriod = () => {
    setAmendData(null);
    setLogPeriodOpen(true);
  };

  const handleAmend = (data: KpiHistoryAmendData) => {
    setAmendData({
      period: data.period,
      value: data.value,
      ragRating: data.ragRating,
      commentary: data.commentary,
      evidenceLinks: data.evidenceLinks,
      measurementId: data.measurementId,
      reportId: data.reportId,
    });
    setLogPeriodOpen(true);
  };

  const handleLogPeriodClose = () => {
    setLogPeriodOpen(false);
    setAmendData(null);
  };

  const submitMeasurement = async (input: CreateKpiMeasurementInput) => {
    const created = (await createMeasurement.mutateAsync({
      contractId,
      type: "kpi-measurement",
      input,
      parentId: input.parentId,
    })) as { id: string } | undefined;
    return created;
  };

  const submitReport = async (input: CreateKpiReportInput) => {
    const created = (await createReport.mutateAsync({
      contractId,
      type: "kpi-report",
      input,
      parentId: input.parentId,
    })) as { id: string } | undefined;
    return created;
  };

  const updateMeasurement = async (
    id: string,
    input: CreateKpiMeasurementInput,
  ) => {
    // The update type needs a `version` — look it up from the loaded
    // measurements. If we can't find it (shouldn't happen; the drawer
    // fetched this data), fall back to 1 which will likely 409.
    const existing = measurements.find((m) => m.id === id);
    const version = existing?.version ?? 1;
    await updateMeasurementMut.mutateAsync({
      contractId,
      type: "kpi-measurement",
      itemId: id,
      input: { ...input, version },
      parentId: input.parentId,
    });
  };

  const updateReport = async (id: string, input: CreateKpiReportInput) => {
    const existing = reports.find((r) => r.id === id);
    const version = existing?.version ?? 1;
    await updateReportMut.mutateAsync({
      contractId,
      type: "kpi-report",
      itemId: id,
      input: { ...input, version },
      parentId: input.parentId,
    });
  };

  const deleteMeasurement = async (id: string) => {
    // Soft-delete via the archive endpoint. Reason string encodes the
    // rollback trigger so audits can recognise it.
    await trackingApi.archive(
      contractId,
      "kpi-measurement",
      id,
      "rollback:partial-write-failure",
    );
  };

  const latestMeasurement = latestByPeriod(measurements);
  const latestMeasurementNumeric = latestMeasurement
    ? parseKpiMeasurementValue(latestMeasurement.value).numeric
    : null;
  const latestMeasurementLabel =
    latestMeasurementNumeric !== null
      ? String(latestMeasurementNumeric)
      : "latest";

  const metaSegments = buildMetaSegments(kpi, latestReport, resolvedUser);
  const metaText = metaSegments.join(" · ");

  const drawerSubtitle = (
    <div className="flex flex-wrap items-center gap-2">
      {latestReport && (
        <TrackingStatusBadge kind="rag" rating={latestReport.ragRating} />
      )}
      {metaSegments.length > 0 && (
        <span
          className="text-caption text-slate-500 dark:text-slate-400"
          data-testid="kpi-meta-line"
          aria-label={`KPI details: ${metaText}`}
          title={metaSegments[metaSegments.length - 1]}
        >
          {metaText}
        </span>
      )}
    </div>
  );

  const drawerActions = (
    <>
      <Button variant="secondary" size="sm" onClick={() => onEdit(kpi)}>
        Edit KPI
      </Button>
      <Button variant="primary" size="sm" onClick={handleOpenLogPeriod}>
        + Log period
      </Button>
    </>
  );

  return (
    <>
      <SlideOverDrawer
        isOpen={isOpen}
        inert={logPeriodOpen}
        onClose={onClose}
        title={kpi.name}
        subtitle={drawerSubtitle}
        actions={drawerActions}
        width="md"
      >
        <div className="space-y-4" data-testid="kpi-detail-drawer">
          {isLoading ? (
            <p
              className="text-body-sm text-slate-500 dark:text-slate-400"
              data-testid="kpi-detail-loading"
            >
              Loading history…
            </p>
          ) : (
            <>
              <KpiDetailStats kpi={kpi} measurements={measurements} />

              <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
                <h3 className="mb-2 text-h4 text-slate-900 dark:text-slate-100">
                  Monthly performance
                </h3>
                <Suspense
                  fallback={
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner size="md" />
                    </div>
                  }
                >
                  <KpiMonthlyChart
                    series={monthlySeries}
                    targetNumeric={targetNumeric}
                    targetLabel={kpi.target}
                    frequency={kpi.measurementFrequency}
                    isBooleanDomain={isBooleanKpi}
                    ariaLabel={`Monthly performance of ${kpi.name}, latest ${latestMeasurementLabel}`}
                  />
                </Suspense>
              </div>

              <KpiMeasurementCriteria kpi={kpi} />

              <KpiHistoryTable
                kpi={kpi}
                measurements={measurements}
                reports={reports}
                range={range}
                isTruncated={isTruncated}
                onAmend={handleAmend}
              />
            </>
          )}
        </div>
      </SlideOverDrawer>

      <KpiLogPeriodEntry
        isOpen={isOpen && logPeriodOpen}
        onClose={handleLogPeriodClose}
        kpi={kpi}
        contractId={contractId}
        measurements={measurements}
        reports={reports}
        submitMeasurement={submitMeasurement}
        submitReport={submitReport}
        updateMeasurement={updateMeasurement}
        updateReport={updateReport}
        deleteMeasurement={deleteMeasurement}
        initialPeriodData={amendData ?? undefined}
      />
    </>
  );
}
